import type { RiskClass } from '@ralph/contracts'
import type { Counting } from './systems.ts'

/**
 * `admin.studio` — the agent definition surfaces: the rail, the editor, the resolve preview
 * and the studio's own tab strip.
 *
 * These are the oldest operator-facing screens in the product and they never had a catalogue
 * (PRD-27 F-14): ninety-seven strings in `AgentEditor.tsx`, thirty in `ResolvePreview.tsx`,
 * sixteen in `AgentList.tsx` and four tab labels compiled into a module-scope table. They are
 * translated for the same reason as everything else — D-14, there are no English islands, and
 * the earlier decision to ship the studio en-only was about TERMINOLOGY, not scope.
 *
 * ── Terminology, which is what that decision was actually about ──
 *
 * This is the administrator plane. AGM-022 scopes the banned words to consumer and maker
 * surfaces, and D-15 keeps the terms of art in English inside Portuguese sentences: `prompt`,
 * `top-k`, `TTL`, `wall clock`, `alias`, `JSON`. A Brazilian operator reads those in English
 * in every tool they run, and "k-superior" is less clear than "top-k", not more.
 *
 * ── One word this catalogue is careful about ──
 *
 * The retrieval rows call pinned knowledge bases *sources*. In Portuguese *fonte* is reserved
 * for a citation and must never mean a base (ADR-0018), so those entries say **base**. It is
 * the one place in this namespace where the English word cannot be followed.
 *
 * ── Two enum maps, and why they are here twice ──
 *
 * `riskLabel` is the administrator's mono spelling — "reversible write" — and is NOT the
 * sentence form the connected-systems queue uses ("Changes something that can be undone").
 * Those are two registers for one enum, on purpose (D-9 amended), and folding them would
 * lose the distinction the first time a translator tidied them.
 */

export const studio = {
  /**
   * The four phase-4 tabs.
   *
   * Kept apart from the maker namespace's own tab labels so the two curation surfaces can be
   * told apart by name: "Curation" is listings (featured order, Official, suspension) and
   * "Capabilities" is the wording backlog for connected capabilities. Two tabs both called
   * curation would be a coin flip every time an administrator went looking for one of them.
   */
  tabs: {
    reports: 'Reports',
    systems: 'Systems',
    capabilities: 'Capabilities',
    policies: 'Profiles'
  },

  /** What each risk class is called in the administrator's own readouts. */
  riskLabel: {
    read: 'read',
    reversible_write: 'reversible write',
    external_communication: 'external communication',
    privileged: 'privileged',
    financial: 'financial',
    destructive: 'destructive'
  } satisfies Record<RiskClass, string>,

  /** The two answers a boolean control can carry. Not `true`/`false`, which are wire values. */
  yes: 'yes',
  no: 'no',

  page: {
    adminOnly: 'Admin access required',
    adminOnlyBody: 'This view shows how every agent on the platform is configured.',
    noneSelected: 'No agent selected',
    noneSelectedLoading: 'Loading the configured agents…',
    noneSelectedBody: 'Pick an agent on the left, or create one — a name, a prompt, and the tools it may reach.'
  },

  list: {
    title: 'Agents',
    new: 'New',
    loading: 'Loading agents…',
    empty:
      'No agents are configured. An agent is stored configuration — a prompt, a tool grant and a budget — not a deploy.',
    off: 'off',
    /** The version and the recency on one line. `when` is already relative-formatted. */
    rowMeta: (version: number, when: string): string => `v${version} · ${when}`,

    create: {
      idLabel: 'Agent id',
      idPlaceholder: 'support-triage',
      idTaken: 'an agent with this id already exists',
      idPattern: 'lowercase letters, numbers and dashes, 2–63 characters',
      nameLabel: 'Display name',
      namePlaceholder: 'defaults to the id',
      promptLabel: 'System prompt',
      promptPlaceholder: 'Who this agent is and what it may assume.',
      submit: 'Create',
      cancel: 'Cancel',
      /**
       * Written into the version history as the change note for the first version.
       *
       * It is prose the product writes on somebody's behalf, so it is translated: a
       * Brazilian administrator reading their own agent's history should not meet an English
       * sentence nobody in the room wrote. The audit trail's `action` and `resourceType`
       * stay verbatim identifiers (LOC-043); this is not one of those.
       */
      note: 'created in the agent studio'
    }
  },

  editor: {
    loading: 'Loading agent…',
    failed: 'This agent could not be loaded',
    retry: 'Try again',
    versionLabel: 'version',
    readOnly: 'read-only — admin role required to save',
    noDefinition: 'This agent has no definition',
    noDefinitionBody: 'Its rows may have been deleted. Restore it from its version history, or recreate it.',

    /** The note under a tool in the grant list: what it may do, and whether it stops for a human. */
    toolNote: (riskLabel: string, needsApproval: boolean): string =>
      needsApproval ? `${riskLabel} · approval` : riskLabel,
    /** The note under a knowledge base in the pinned-sources list. */
    documentCount: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} docs`,
    /** A granted name that no longer exists in the registry, so it has no words of its own. */
    unknownTool: 'not in the tool registry',

    expand: 'Expand the definition',
    collapse: 'Collapse the definition',
    definition: 'Definition',
    disabledBadge: 'disabled',
    unsavedBadge: 'unsaved',
    savingBadge: 'saving',
    enabledLabel: 'enabled',
    /** The definition's own line: its version, who it applies to, and what it sets. */
    layerMeta: (version: number, overrides: string): string =>
      `v${version} · the same for everyone who can use it · ${overrides}`,
    overridesNothing: 'overrides nothing',
    /** `fields` is already joined by the one `Intl.ListFormat` in the repository (LOC-016). */
    overridesSets: (fields: string): string => `sets ${fields}`,

    sections: {
      identity: 'Identity',
      instructions: 'Instructions',
      model: 'Model',
      tools: 'Tools',
      retrieval: 'Retrieval',
      memory: 'Memory',
      budget: 'Budget',
      delegation: 'Delegation',
      metadata: 'Metadata'
    },

    displayNameLabel: 'display name',
    descriptionLabel: 'description',
    descriptionPlaceholder: 'what this agent is for',

    promptLabel: 'system prompt',
    promptPlaceholder: 'The agent is minimally a prompt. Write it here.',
    promptMissing: 'no prompt is set — the agent will not resolve',
    charCount: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} chars`,
    prefixLabel: 'prompt prefix',
    prefixPlaceholder: 'a standing instruction, e.g. always cite the internal ticket number',
    prefixHint: 'a standing instruction placed before the prompt on every run, after any the deployment sets',

    modelAliasLabel: 'model alias',
    answerModeLabel: 'answer mode',

    builtinToolsLabel: 'builtin tools',
    builtinToolsHint: 'an allow-list — a tool the agent was not granted cannot be reached however it is prompted',
    connectedToolsLabel: 'connected tools',
    connectedToolsHint: 'server-qualified names, e.g. jira/create_issue',
    connectedToolsPlaceholder: 'server/tool',

    /** *sources* here means pinned knowledge bases, which Portuguese must not call *fontes*. */
    sourcesLabel: 'pinned sources',
    sourcesHint: 'blank means every source the person may read',

    memoryReadLabel: 'read',
    memoryWriteLabel: 'write',
    /**
     * The memory write modes, as words rather than as the wire tokens the select used to
     * render (PRD-27 D-9, LOC-018). The English spellings are unchanged, which is the point:
     * moving a value into a label map must not move the value.
     */
    memoryWrite: {
      off: 'off',
      propose: 'propose',
      auto: 'auto'
    },

    metadataLabel: 'operator metadata (json object)',
    metadataPlaceholder: '{}',
    metadataNotObject: 'metadata must be a JSON object',
    metadataNotJson: 'not valid JSON yet',

    historyShow: 'Version history',
    historyHide: 'Hide history',
    historyLoading: 'Loading history…',
    historyEmpty: 'No recorded versions.',
    historyNoNote: 'no note',
    historyLoad: 'Load',

    staleTitle: 'Someone else saved this layer first',
    staleBody: (version: number): string =>
      `You edited version ${version} and the stored layer has moved past it. Nothing was overwritten. Your copy is still on screen — copy anything you need out of it, then reload to start from theirs.`,
    staleReload: 'Reload this layer',
    staleKeep: 'Keep editing',

    changeNotePlaceholder: 'what changed and why — kept in the version history',
    changeNoteLabel: 'Change note',
    savedNote: 'saved',
    revert: 'Revert',
    save: 'Save',

    /** What a blank field falls back to when the platform's own number is not knowable here. */
    platformDefault: 'platform default',
    /** The empty option's label carries the inherited value, so the menu need not be opened. */
    inheritOption: (inherited: string): string => `inherit · ${inherited}`,

    /**
     * A list field's state, as a toggle rather than as its contents: an empty allow-list is a
     * real, meaningful value ("this agent gets nothing"), so blank cannot mean inherit here.
     */
    listInherited: 'inherited · override',
    listOverriding: 'overriding · inherit instead',
    listFromLower: 'inherited from a lower layer',
    listEmpty: 'nothing granted',
    listRemove: (value: string): string => `Remove ${value}`,
    listAddTo: (label: string): string => `Add to ${label}`,
    listAdd: 'Add',

    budgetFields: {
      maxSteps: 'max steps',
      maxModelCalls: 'max model calls',
      maxToolCalls: 'max tool calls',
      maxDelegations: 'max delegations',
      maxCostUsd: 'max cost (usd)',
      maxWallClockMs: 'max wall clock (ms)'
    },
    delegationFields: {
      maxDepth: 'max depth',
      maxParallel: 'max parallel',
      remoteAgents: 'remote agents',
      remoteAgentsHint: 'registry slugs this agent may hand work to',
      remoteAgentsPlaceholder: 'remote agent slug'
    },

    /** Which parts of the body a definition sets, named for the `sets …` line. */
    overrideFields: {
      systemPrompt: 'prompt',
      systemPromptPrefix: 'prefix',
      modelAlias: 'model',
      answerMode: 'answer mode',
      tools: 'tools',
      retrieval: 'retrieval',
      memory: 'memory',
      budget: 'budget',
      delegation: 'delegation',
      meta: 'metadata'
    }
  },

  preview: {
    title: 'Resolved agent',
    resolving: 'resolving',
    degraded: 'degraded',
    lead: 'What every run of this agent drives under — the same for everyone who can use it.',
    failed: 'This agent does not resolve',
    loading: 'Resolving…',
    empty: 'Nothing resolved yet',
    emptyBody: 'The request has not started. Check the connection and try again.',

    definitionEyebrow: 'definition',
    platformOnly: 'platform only',
    policyEyebrow: 'policy',
    versionBadge: (version: number): string => `v${version}`,
    policyVersion: (name: string, version: number): string => `${name} v${version}`,

    showPrompt: 'Show the assembled prompt',
    hidePrompt: 'Hide the assembled prompt',

    /**
     * An empty set is a state rather than a list and keeps its own word — an empty
     * `Intl.ListFormat` renders the empty string, which reads as a row that failed to load.
     */
    none: 'none',
    everySource: 'every source the user may read',
    pinned: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} pinned`,
    charCount: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} chars`,

    sections: {
      instructions: 'Instructions',
      model: 'Model',
      tools: 'Tools',
      retrieval: 'Retrieval',
      memory: 'Memory',
      budget: 'Budget',
      humanInLoop: 'Human in the loop',
      delegation: 'Delegation'
    },

    /**
     * Every row label, keyed. `SECTIONS` in `ResolvePreview.tsx` is a module-scope table —
     * it is the reading order of the panel, not state — so it holds these KEYS and the
     * component looks each one up from the active pack at render (PRD-27 F-3, LOC-012).
     */
    rows: {
      prompt: 'prompt',
      prefix: 'prefix',
      alias: 'alias',
      answerMode: 'answer mode',
      builtin: 'builtin',
      connected: 'connected',
      strategy: 'strategy',
      topK: 'top k',
      minScore: 'min score',
      sources: 'sources',
      memoryRead: 'read',
      memoryWrite: 'write',
      steps: 'steps',
      modelCalls: 'model calls',
      toolCalls: 'tool calls',
      delegations: 'delegations',
      cost: 'cost',
      wallClock: 'wall clock',
      extraRisks: 'extra risks',
      rounds: 'rounds',
      ttl: 'ttl',
      remoteAgents: 'remote agents',
      maxDepth: 'max depth',
      maxParallel: 'max parallel'
    },

    /** Where a value came from, said rather than left for an operator to infer. */
    originPolicy: (name: string, version: number): string => `policy ${name} v${version}`,
    /**
     * A layer asked for something and got something else: the policy's ceiling won. Its own
     * entry rather than a prefix concatenated onto the one above, because "capped by" is a
     * sentence opening and no language places those by string addition (LOC-014).
     */
    originCapped: (name: string, version: number): string => `capped by policy ${name} v${version}`,
    originConcatenated: 'concatenated',
    originPlatform: 'platform',
    originDefinition: 'definition'
  },

  /**
   * The studio's own frame, and its curation tab (PRD-27: the last block to leave
   * `maker-strings.ts`, which is now gone).
   *
   * An administrator decides what a DEFINITION may do and who may use it, so the banned
   * vocabulary does not apply here: AGM-022 scopes that ban to consumer and maker copy, and it
   * exists to protect the people who did not choose to learn those words — not to hide them
   * from the people whose job is to weigh them.
   */
  frame: {
    title: 'Agent studio',
    lead: 'Definitions, curation and the marketplace policy that governs them.',
    adminOnly: 'Admin access required',
    adminOnlyBody:
      'This view decides what every agent on the platform may do, and who may publish one. It is limited to administrators.',
    tabs: {
      definitions: 'Definitions',
      curation: 'Curation',
      settings: 'Settings'
    }
  },

  curation: {
    title: 'Listings',
    lead: 'Featured order, the Official badge, and suspension. None of it is reachable from a maker request.',
    loading: 'Loading listings…',
    empty: 'No listings yet',
    emptyBody: 'Published and draft listings appear here as makers create them.',
    unavailable: 'The listing index is not available on this deployment yet.',
    unavailableBody: 'GET /api/admin/marketplace/listings has not been implemented — curation actions are disabled.',
    featureRank: 'rank',
    featureClear: 'Unpin',
    officialOn: 'Grant Official',
    officialOff: 'Revoke Official',
    suspend: 'Suspend',
    unsuspend: 'Lift suspension',
    suspendTitle: (name: string) => `Suspend ${name}`,
    suspendLead: 'Suspension blocks new runs platform-wide, including through the API, within the 60-second SLO.',
    reasonPublic: 'Reason shown to people (consumer-safe)',
    reasonPublicPlaceholder: 'This agent is unavailable while we check something.',
    reasonFull: 'Full reason (owner and audit only)',
    reasonFullPlaceholder: 'What happened, what you verified, and what has to change before it comes back.',
    replacement: 'Replacement agent id (optional)',
    replacementPlaceholder: 'expense-buddy-v2',
    confirmSuspend: 'Suspend now',
    cancel: 'Cancel',
    failed: 'That action failed.',
    needsOwnerTitle: 'Needs an owner',
    needsOwnerLead:
      'Nobody maintains these any more — their owner left, or their group has no managers. They keep serving until they have an owner.'
  }
}
